import { supabase } from '../lib/supabase';

/**
 * Admin Alerts Service
 * Manages admin notification alerts with real-time updates
 */

export const adminAlertsService = {
  /**
   * Get all admin alerts with pagination
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Alerts data with pagination info
   */
  async getAlerts({ page = 1, limit = 20, unreadOnly = false } = {}) {
    try {
      const offset = (page - 1) * limit;
      
      let query = supabase?.from('admin_alerts')?.select('*', { count: 'exact' })?.order('created_at', { ascending: false })?.range(offset, offset + limit - 1);

      if (unreadOnly) {
        query = query?.eq('is_read', false);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        alerts: data || [],
        total: count || 0,
        page,
        limit,
        totalPages: Math.ceil((count || 0) / limit)
      };
    } catch (error) {
      console.error('Error fetching admin alerts:', error);
      throw error;
    }
  },

  /**
   * Get unread alerts count
   * @returns {Promise<number>} Number of unread alerts
   */
  async getUnreadCount() {
    try {
      const { count, error } = await supabase?.from('admin_alerts')?.select('*', { count: 'exact', head: true })?.eq('is_read', false);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  },

  /**
   * Mark alert as read
   * @param {string} alertId - Alert ID to mark as read
   * @returns {Promise<Object>} Updated alert
   */
  async markAsRead(alertId) {
    try {
      const { data, error } = await supabase?.from('admin_alerts')?.update({ is_read: true })?.eq('id', alertId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error marking alert as read:', error);
      throw error;
    }
  },

  /**
   * Mark multiple alerts as read
   * @param {Array<string>} alertIds - Array of alert IDs
   * @returns {Promise<Array>} Updated alerts
   */
  async markMultipleAsRead(alertIds) {
    try {
      const { data, error } = await supabase?.from('admin_alerts')?.update({ is_read: true })?.in('id', alertIds)?.select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error marking multiple alerts as read:', error);
      throw error;
    }
  },

  /**
   * Mark all alerts as read
   * @returns {Promise<Array>} Updated alerts
   */
  async markAllAsRead() {
    try {
      const { data, error } = await supabase?.from('admin_alerts')?.update({ is_read: true })?.eq('is_read', false)?.select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error marking all alerts as read:', error);
      throw error;
    }
  },

  /**
   * Get alerts by type
   * @param {string} type - Alert type
   * @param {number} limit - Number of alerts to fetch
   * @returns {Promise<Array>} Alerts of specified type
   */
  async getAlertsByType(type, limit = 10) {
    try {
      const { data, error } = await supabase?.from('admin_alerts')?.select('*')?.eq('type', type)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching alerts by type:', error);
      throw error;
    }
  },

  /**
   * Get alerts by entity
   * @param {string} entityType - Entity type (container, shipment, etc.)
   * @param {string} entityId - Entity ID
   * @returns {Promise<Array>} Alerts for specified entity
   */
  async getAlertsByEntity(entityType, entityId) {
    try {
      const { data, error } = await supabase?.from('admin_alerts')?.select('*')?.eq('entity_type', entityType)?.eq('entity_id', entityId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching alerts by entity:', error);
      throw error;
    }
  },

  /**
   * Delete old read alerts
   * @param {number} daysOld - Delete alerts older than this many days
   * @returns {Promise<number>} Number of deleted alerts
   */
  async deleteOldAlerts(daysOld = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate?.setDate(cutoffDate?.getDate() - daysOld);

      const { data, error } = await supabase?.from('admin_alerts')?.delete()?.eq('is_read', true)?.lt('created_at', cutoffDate?.toISOString())?.select();

      if (error) throw error;
      return data?.length || 0;
    } catch (error) {
      console.error('Error deleting old alerts:', error);
      throw error;
    }
  }
};